import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, Icon, IconButton, makeStyles, Tooltip, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import { Form, Formik, useFormikContext } from "formik";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { AdviceDelivery, AdviserDetails, ClientStatuses, DisclosureStatementDto, disclosureStatementSchema, Product, UpsertDefautlDisclosureTemplateCommand } from "../../../api";
import { useSharedStyles } from "../../../common/sharedStyles";
import { useAppDispatch, useAppSelector } from "../../../store";
import { previewDisclosureStatementAsync, upsertDefaultDisclosureTemplateAsync } from "../../../store/adviser";
import { Colors, HelpTooltip, SubtitleText, TitleText } from "../../controls/helpers";
import TextArea from "../TextArea";
import LogoUpload from "./LogoUpload";
import {
  aboutMeTemplate,
  complaintsTemplate,
  conflictOfInterestTemplate,
  convertToHtml,
  feesTemplate,
  initializeDefaultTemplate,
  introductionTemplate,
  natureAndScopeOfAdviceTemplate,
  reliabilityEventsTemplate,
} from "./TemplateText";

const DisclosureTemplate = ({ history, location }: RouteComponentProps) => {
  const { adviserDetails, client, disclosureTemplate } = useAppSelector((state) => state.adviser);
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState({});

  const handleSubmit = useCallback(
    async (values: UpsertDefautlDisclosureTemplateCommand) => {
      const isKsOnly = client?.products?.length === 1 && client.products.includes(Product.KiwiSaver);
      setHasChanged(false);
      await dispatch(upsertDefaultDisclosureTemplateAsync(values));

      if (client?.adviceDelivery === AdviceDelivery.InPerson && client?.clientStatus === ClientStatuses.None) {
        history.push(`/adviser/sat-application/${client.clientId}`);
      } else {
        history.push(`/adviser/new-application/${isKsOnly ? "kiwisaver" : "application-details"}`, location.state);
      }
    },
    [client, dispatch, history, location.state]
  );

  const previewPdf = useCallback((disclosureStatements: DisclosureStatementDto[]) => dispatch(previewDisclosureStatementAsync({ disclosureStatements })), [dispatch]);

  const ChangedValues = () => {
    const { dirty } = useFormikContext<UpsertDefautlDisclosureTemplateCommand>();

    useEffect(() => {
      setHasChanged(dirty);
    }, [hasChanged, dirty]);

    return null;
  };

  useEffect(() => {
    let disclosureStatements: DisclosureStatementDto[] | undefined = disclosureTemplate?.disclosureStatements;
    if (!disclosureStatements) disclosureStatements = initializeDefaultTemplate(adviserDetails!);
    setInitialValues({ disclosureStatements });
  }, [disclosureTemplate]);

  return (
    <Box px={11} py={3}>
      <Grid container spacing={3}>
        <Grid item sm={9} xs={12}>
          <TitleText hideMarginBottom>Disclosure template</TitleText>
          <SubtitleText>Enter details to appear in the disclosure statement.</SubtitleText>
        </Grid>
        <Grid item sm={3} xs={12}>
          <LogoUpload />
        </Grid>
      </Grid>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={disclosureStatementSchema} enableReinitialize>
        {({ handleChange, setFieldValue, values }) => (
          <Form className={sharedClasses.form}>
            {values.disclosureStatements?.map((statement, index) => (
              <DisclosureStatement key={index} index={index} statement={statement} adviserDetails={adviserDetails} handleChange={handleChange} setFieldValue={setFieldValue} />
            ))}
            <ChangedValues />
            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={() => previewPdf(values.disclosureStatements!)} className={classes.button} style={{ padding: "8px 13px 10px" }}>
                Preview
              </Button>
              <Button variant="contained" color="primary" type="submit" disableElevation className={classes.button}>
                Save
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

function DisclosureStatement({
  statement,
  index,
  handleChange,
  adviserDetails,
  setFieldValue,
}: {
  statement: DisclosureStatementDto;
  index: number;
  adviserDetails?: AdviserDetails;
  handleChange?: (event: ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
  const classes = useStyles();
  const [isEnabledForEdit, setIsEnabledForEdit] = useState(false);
  const reset = () => {
    let fieldValue;
    switch (statement.name?.toLowerCase()) {
      case "About me".toLowerCase():
        fieldValue = aboutMeTemplate(adviserDetails);
        break;
      case "Introduction".toLowerCase():
        fieldValue = introductionTemplate();
        break;
      case "Nature and scope of my advice".toLowerCase():
        fieldValue = natureAndScopeOfAdviceTemplate("<<FAP>>");
        break;
      case "Fees".toLowerCase():
        fieldValue = feesTemplate();
        break;
      case "Reliability events".toLowerCase():
        fieldValue = reliabilityEventsTemplate("<<FAP>>");
        break;
      case "Conflicts of interest, commissions, and incentives".toLowerCase():
        fieldValue = conflictOfInterestTemplate("<<FAP>>");
        break;
      case "Complaints".toLowerCase():
        fieldValue = complaintsTemplate("", "", "", "", "", "", "", "");
        break;
      default:
        break;
    }

    fieldValue && setFieldValue(`disclosureStatements.${index}.content`, fieldValue);
  };

  return (
    <>
      <Box className={classes.headerContainer}>
        <Typography color="textSecondary" className={classes.title}>
          {statement.name}
          {statement.name?.toLowerCase() === "Reliability events".toLowerCase() && (
            <HelpTooltip
              title="You must provide information about any recent (normally in the last five years) 
                        material instances where you or the financial advice provider have been publicly disciplined, have a relevant conviction or civil proceedings and, 
                        in the case of financial advisers, any recent bankruptcies or insolvencies.
                        Both the financial advice provider and the financial adviser may have reliability events, which must all be disclosed.
                        If you are concerned that you may have a reliability event to disclose, we would strongly encourage you to seek legal advice."
            />
          )}
        </Typography>
        <Icon className={classes.headerIcon}>
          <CheckIcon />
        </Icon>
      </Box>
      <Box>
        <FormControl component="fieldset" className={classes.options}>
          <FormControlLabel
            control={<Checkbox name={`disclosureStatements.${index}.isIncluded`} onChange={handleChange} />}
            label={<span className={classes.checkboxLabel}>Include</span>}
            style={{ marginRight: 80 }}
            checked={statement.isIncluded}
          />
          <FormControlLabel checked={isEnabledForEdit} control={<Checkbox onChange={(_) => setIsEnabledForEdit(!isEnabledForEdit)} />} label={<span className={classes.checkboxLabel}>Edit</span>} />
        </FormControl>
        <Tooltip title="Reset">
          <IconButton className={classes.reset} onClick={reset}>
            <RotateLeftIcon />
          </IconButton>
        </Tooltip>
        {isEnabledForEdit && <TextArea name={`disclosureStatements.${index}.content`} />}
        {!isEnabledForEdit && <Box dangerouslySetInnerHTML={{ __html: convertToHtml(statement.content!) }} />}
      </Box>
    </>
  );
}

const useStyles = makeStyles(
  {
    title: {
      color: Colors.Black,
      fontSize: 16,
      fontWeight: 400,
      display: "flex",
    },
    options: {
      flexDirection: "row",
      border: "0px solid transparent",
    },
    headerContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      padding: "5px 15px 10px 0px",
      borderBottom: `1px solid ${Colors.Grey}`,
      margin: "20px 0 10px",
    },
    button: {
      marginRight: 10,
      padding: "9px 13px 11px",
      width: 130,
    },
    headerIcon: {
      fontSize: 15,
      "& > svg": {
        width: 18,
        height: 18,
      },
    },
    checkboxLabel: {
      fontSize: 16,
    },
    reset: {
      float: "right",
    },
  },
  { name: "DisclosureTemplate" }
);

export default DisclosureTemplate;
